<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import FluentIcon from 'shared/components/FluentIcon/DashboardIcon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { activityIconFor } from './activityTypes';

const props = defineProps({
  record: { type: Object, required: true },
  // `executions` only exists on the detail payload (GET .../:id), fetched on
  // open. Null while it is still in flight.
  detail: { type: Object, default: null },
  isLoadingDetail: { type: Boolean, default: false },
  isAdministrator: { type: Boolean, default: false },
});

const emit = defineEmits([
  'close',
  'edit',
  'duplicate',
  'toggleActive',
  'delete',
]);

const { t } = useI18n();
const prefix = key => t(`PIPELINE.SEQUENCE_DEFINITIONS.${key}`);

const hasWebhookStep = computed(
  () =>
    Array.isArray(props.record.steps) &&
    props.record.steps.some(step => step?.activity_type === 'webhook')
);

const canManage = computed(
  () => props.isAdministrator || !hasWebhookStep.value
);

// The offset shown next to each step is cumulative: every delay is relative to
// the previous step, the same way the backend schedules them.
const steps = computed(() => {
  if (!Array.isArray(props.record.steps)) return [];

  let offsetDays = 0;
  return props.record.steps.map((step, index) => {
    offsetDays +=
      (Number(step?.delay_days) || 0) + (Number(step?.delay_hours) || 0) / 24;
    return {
      key: `${index}-${step?.activity_type || 'unknown'}`,
      icon: activityIconFor(step?.activity_type),
      title: step?.title || prefix('FORM_STEP_NUMBER', { n: index + 1 }),
      isWebhook: step?.activity_type === 'webhook',
      dayLabel: t('PIPELINE.SEQUENCE_DEFINITIONS.DETAIL_STEP_DAY', {
        n: Math.round(offsetDays),
      }),
    };
  });
});

const executionStats = computed(() => {
  const executions = props.detail?.executions;
  if (!executions) return null;

  return [
    {
      key: 'total',
      label: prefix('DETAIL_EXECUTIONS_TOTAL'),
      value: executions.total ?? 0,
    },
    {
      key: 'active',
      label: prefix('DETAIL_EXECUTIONS_ACTIVE'),
      value: executions.active ?? 0,
    },
    {
      key: 'completed',
      label: prefix('DETAIL_EXECUTIONS_COMPLETED'),
      value: executions.completed ?? 0,
    },
    {
      key: 'cancelled',
      label: prefix('DETAIL_EXECUTIONS_CANCELLED'),
      value: executions.cancelled ?? 0,
    },
  ];
});
</script>

<template>
  <aside
    class="flex flex-col flex-shrink-0 w-full bg-n-solid-1 border-n-weak xl:w-[22.5rem] xl:border-l max-xl:fixed max-xl:inset-y-0 max-xl:z-40 max-xl:max-w-sm max-xl:shadow-xl ltr:max-xl:right-0 rtl:max-xl:left-0"
    :aria-label="record.name"
  >
    <header
      class="flex gap-3 justify-between items-start px-4 py-4 border-b border-n-weak"
    >
      <div class="min-w-0">
        <h3 class="text-sm font-semibold truncate text-n-slate-12">
          {{ record.name }}
        </h3>
        <span
          class="inline-flex gap-1.5 items-center mt-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold"
          :class="
            record.active
              ? 'bg-n-teal-3 text-n-teal-11'
              : 'bg-n-alpha-2 text-n-slate-10'
          "
        >
          <span class="w-1.5 h-1.5 rounded-full bg-current" />
          {{
            record.active ? prefix('STATUS_ACTIVE') : prefix('STATUS_INACTIVE')
          }}
        </span>
      </div>
      <Button
        icon="i-lucide-x"
        color="slate"
        size="xs"
        class="flex-shrink-0 rounded-md"
        :aria-label="prefix('DETAIL_CLOSE')"
        @click="emit('close')"
      />
    </header>

    <div class="overflow-y-auto flex-1 min-h-0">
      <section class="px-4 py-4 border-b border-n-weak">
        <h4
          class="mb-3 text-[10px] font-semibold tracking-wider uppercase text-n-slate-10"
        >
          {{ prefix('FLOW_LABEL') }}
        </h4>
        <ol v-if="steps.length" class="flex flex-col gap-2">
          <li
            v-for="step in steps"
            :key="step.key"
            class="flex gap-3 items-center"
          >
            <span
              class="flex flex-shrink-0 justify-center items-center rounded-lg size-7"
              :class="
                step.isWebhook
                  ? 'bg-n-amber-3 text-n-amber-11'
                  : 'bg-n-alpha-2 text-n-slate-11'
              "
            >
              <FluentIcon :icon="step.icon" size="14" />
            </span>
            <span class="flex-1 text-xs truncate text-n-slate-12">
              {{ step.title }}
            </span>
            <span class="text-[11px] text-n-slate-10 tabular-nums">
              {{ step.dayLabel }}
            </span>
          </li>
        </ol>
        <p v-else class="text-xs text-n-slate-10">
          {{ prefix('DETAIL_NO_STEPS') }}
        </p>
      </section>

      <section class="px-4 py-4 border-b border-n-weak">
        <h4
          class="mb-3 text-[10px] font-semibold tracking-wider uppercase text-n-slate-10"
        >
          {{ prefix('DETAIL_EXECUTIONS_TITLE') }}
        </h4>
        <div
          v-if="isLoadingDetail"
          class="grid grid-cols-4 gap-2"
          role="status"
          aria-live="polite"
        >
          <div
            v-for="n in 4"
            :key="n"
            class="h-[3.25rem] rounded-lg animate-pulse bg-n-alpha-2 motion-reduce:animate-none"
          />
          <span class="sr-only">{{ prefix('DETAIL_LOADING') }}</span>
        </div>
        <div v-else-if="executionStats" class="grid grid-cols-4 gap-2">
          <div
            v-for="stat in executionStats"
            :key="stat.key"
            class="py-2 px-1 text-center rounded-lg border border-n-weak"
          >
            <p class="text-base font-semibold text-n-slate-12 tabular-nums">
              {{ stat.value }}
            </p>
            <p class="text-[9px] uppercase tracking-wide text-n-slate-10">
              {{ stat.label }}
            </p>
          </div>
        </div>
      </section>
    </div>

    <!-- Grid, not flex-wrap: at 360px the four actions overflow the panel and
         the last one ends up under the support widget. -->
    <footer class="grid grid-cols-2 gap-2 px-4 py-4 border-t border-n-weak">
      <Button
        v-if="canManage"
        :label="prefix('BTN_EDIT')"
        color="blue"
        size="sm"
        @click="emit('edit')"
      />
      <Button
        v-if="canManage"
        :label="prefix('BTN_DUPLICATE')"
        color="slate"
        variant="faded"
        size="sm"
        @click="emit('duplicate')"
      />
      <Button
        v-if="canManage"
        :label="
          record.active ? prefix('BTN_DEACTIVATE') : prefix('BTN_ACTIVATE')
        "
        color="slate"
        variant="faded"
        size="sm"
        @click="emit('toggleActive')"
      />
      <Button
        v-if="isAdministrator"
        :label="prefix('BTN_DELETE')"
        color="ruby"
        variant="faded"
        size="sm"
        @click="emit('delete')"
      />
      <p v-if="!canManage" class="col-span-2 text-xs text-n-slate-10">
        {{ prefix('WEBHOOK_ADMIN_REQUIRED') }}
      </p>
    </footer>
  </aside>
</template>
